import { useEffect } from "react";
import Lenis from "lenis";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const NAV_LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/#contact", label: "Contact", testId: "nav-contact-link", hash: true },
  { to: "/privacy-policy", label: "Privacy Policy", testId: "nav-privacy-link" },
  { to: "/terms-of-use", label: "Terms of Use", testId: "nav-terms-link" },
];

export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
};

export const LogoMark = ({ className = "h-7 w-7" }) => (
  <svg
    viewBox="0 0 64 64"
    className={className}
    role="img"
    aria-label="Bright Frontier Labs logo"
    data-testid="brand-logo-mark"
  >
    <rect width="64" height="64" rx="14" fill="#0A0A0A" />
    <circle cx="32" cy="36" r="13" fill="#F8F9FA" />
    <rect x="12" y="37" width="40" height="5" fill="#0A0A0A" />
    <rect x="22" y="48" width="20" height="3" rx="1.5" fill="#F8F9FA" />
  </svg>
);

const Header = () => (
  <header
    data-testid="site-header"
    className="sticky top-0 z-50 border-b border-black/5 bg-white/70 backdrop-blur-xl"
  >
    <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-8">
      <Link
        to="/"
        data-testid="header-brand-link"
        className="flex items-center gap-2.5 font-heading text-lg font-bold tracking-tight text-[#0A0A0A] transition-colors duration-200 hover:text-[#4B5563]"
      >
        <LogoMark />
        Bright Frontier Labs
      </Link>
      <nav data-testid="header-nav" className="flex items-center gap-1 md:gap-2">
        {NAV_LINKS.map((link) =>
          link.hash ? (
            <Link
              key={link.testId}
              to={link.to}
              data-testid={link.testId}
              className="rounded-full px-3 py-2 text-xs font-medium text-[#4B5563] transition-colors duration-200 hover:bg-black/5 hover:text-[#0A0A0A] md:px-4 md:text-sm"
            >
              {link.label}
            </Link>
          ) : (
            <NavLink
              key={link.testId}
              to={link.to}
              end={link.to === "/"}
              data-testid={link.testId}
              className={({ isActive }) =>
                `rounded-full px-3 py-2 text-xs font-medium transition-colors duration-200 md:px-4 md:text-sm ${
                  isActive
                    ? "bg-[#0A0A0A] text-white"
                    : "text-[#4B5563] hover:bg-black/5 hover:text-[#0A0A0A]"
                }`
              }
            >
              {link.label}
            </NavLink>
          )
        )}
      </nav>
    </div>
  </header>
);

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white">
    <div className="mx-auto max-w-7xl px-6 py-20 md:px-8">
      <p
        data-testid="footer-brand"
        className="font-heading text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl"
      >
        Bright Frontier Labs
      </p>
      <p className="mt-4 max-w-md text-base leading-relaxed text-white/60">
        An independent mobile app publisher crafting next-generation mobile
        experiences.
      </p>

      <div className="mt-12 grid gap-10 border-t border-white/10 pt-10 md:grid-cols-3">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-white/40">
            Company
          </h3>
          <ul className="mt-4 space-y-3">
            <li>
              <Link
                to="/"
                data-testid="footer-home-link"
                className="text-sm text-white/80 transition-colors duration-200 hover:text-white"
              >
                Home
              </Link>
            </li>
            <li>
              <Link
                to="/#contact"
                data-testid="footer-contact-link"
                className="text-sm text-white/80 transition-colors duration-200 hover:text-white"
              >
                Contact
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-white/40">
            Legal
          </h3>
          <ul className="mt-4 space-y-3">
            <li>
              <Link
                to="/privacy-policy"
                data-testid="footer-privacy-link"
                className="text-sm text-white/80 transition-colors duration-200 hover:text-white"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms-of-use"
                data-testid="footer-terms-link"
                className="text-sm text-white/80 transition-colors duration-200 hover:text-white"
              >
                Terms of Use
              </Link>
            </li>
            <li>
              <Link
                to="/data-deletion"
                data-testid="footer-data-deletion-link"
                className="text-sm text-white/80 transition-colors duration-200 hover:text-white"
              >
                Data Deletion
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-widest text-white/40">
            Support
          </h3>
          <a
            href="mailto:support@brightfrontierlabs.com"
            data-testid="footer-support-email-link"
            className="mt-4 inline-flex items-center gap-1 text-sm text-white/80 transition-colors duration-200 hover:text-white"
          >
            support@brightfrontierlabs.com
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      <p
        data-testid="footer-copyright"
        className="mt-16 text-xs text-white/40"
      >
        &copy; {new Date().getFullYear()} Bright Frontier Labs. All rights
        reserved.
      </p>
    </div>
  </footer>
);

export default function Layout() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, anchors: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#F8F9FA] font-body text-[#0A0A0A]">
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
